import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  Bell,
  Globe,
  Wrench,
  Shield,
  Users,
  Bot,
  ScrollText,
} from 'lucide-react';

export interface SettingsTab {
  label: string;
  /** The tab's own URL — the page's pre-workspace URL, unchanged. */
  route: string;
  /** Only show when this vertical is enabled. Omit for always-visible tabs. */
  vertical?: string;
  /** Only show for admin-level roles. */
  adminOnly?: boolean;
}

export interface SettingsWorkspace {
  key: string;
  label: string;
  icon: LucideIcon;
  tabs: SettingsTab[];
}

/**
 * Single source of truth for Settings navigation.
 * Used by the sidebar (desktop), the mobile hub, and each workspace's tab strip.
 *
 * One sidebar entry per workspace; a workspace's pages are its tabs. Every tab
 * keeps the URL its page had before workspaces existed, so no bookmark moves.
 * A workspace is shown when at least one of its tabs is, and its sidebar entry
 * opens the first visible tab.
 */
export const SETTINGS_WORKSPACES: SettingsWorkspace[] = [
  {
    key: 'organization',
    label: 'Organization',
    icon: Building2,
    tabs: [
      { label: 'Company Profile', route: '/settings/company-profile' },
      { label: 'Locations', route: '/settings/locations' },
      { label: 'Appearance', route: '/settings/appearance' },
      // Sender identity, so it sits with the company rather than Notifications.
      { label: 'Email Branding', route: '/settings/email-branding' },
      // The company's Overview layout (api#873). The writes are requireRole('admin'),
      // which admits owner as isAdminRole does, so adminOnly matches the API here.
      { label: 'Dashboard', route: '/settings/dashboard', adminOnly: true },
    ],
  },
  {
    key: 'notifications',
    label: 'Notifications',
    icon: Bell,
    tabs: [{ label: 'Notifications', route: '/settings/notifications' }],
  },
  {
    key: 'finance',
    label: 'Finance & region',
    icon: Globe,
    tabs: [
      { label: 'Regional & Tax', route: '/settings/regional-tax' },
      { label: 'Invoice Settings', route: '/settings/invoice' },
      { label: 'Pay Periods', route: '/settings/pay-periods', adminOnly: true },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: Wrench,
    tabs: [
      // Ticket numbering lives in a Numbering section at the top of this page —
      // same endpoint, same Save. It is not its own tab.
      { label: 'Field Tickets', route: '/settings/field-tickets' },
      { label: 'Job Numbering', route: '/settings/job-numbering' },
      // Cross-vertical: every tenant offers services. Deliberately ungated.
      { label: 'Services', route: '/settings/services' },
      // Cross-vertical: the line_item_type enum is one vocabulary for every
      // tenant; this page only chooses which of it to offer. Ungated.
      { label: 'Line Item Types', route: '/settings/line-item-types' },
      // Cross-vertical, like Equipment itself. Deliberately ungated — and
      // deliberately NOT `adminOnly`: that flag resolves through `isAdminRole`,
      // which admits `owner`, while the API's writes match 'admin' literally
      // and would 403 them. The list is ungated on the API and useful to
      // everyone; the page hides the write controls itself.
      { label: 'Equipment Types', route: '/settings/equipment-types' },
      // BOP certificate limits (api#1268). Cross-vertical, like Equipment, so no
      // vertical gate; not `adminOnly` for the Equipment Types reason above — the
      // read is open to every internal role and the page hides its writes itself.
      { label: 'Pressure Control', route: '/settings/pressure-control' },
      // Radioactive sources (api#1334, RAD-1): leak-test interval, warning days,
      // dose level, out-of-shield hours. Shared module, so no vertical gate; not
      // `adminOnly`, for the Equipment Types reason above.
      { label: 'Radiation', route: '/settings/radiation' },
      // Also carries trucking Positions (trucking_positions), which moved here
      // from People & access: trucking data belongs to trucking tenants.
      { label: 'Trucking', route: '/settings/trucking', vertical: 'trucking' },
      { label: 'Rentals', route: '/settings/rentals', vertical: 'surface_rentals' },
      { label: 'Pressure Testing', route: '/settings/pressure-testing', vertical: 'pressure_testing' },
      // GET/PUT /ndt-inspection/settings (api#1134): ticket mode, acceptance code, warn days, dose level.
      { label: 'NDT Inspection', route: '/settings/ndt-inspection', vertical: 'ndt_inspection' },
      // GET/PUT /pipeline-facility/settings + per-customer settings (api#1196): ticket mode, code, cal warn days, signer.
      { label: 'Pipeline & Facility', route: '/settings/pipeline-facility', vertical: 'pipeline_facility' },
      // GET/PUT /well-testing/settings + per-customer settings (api#1241): ticket mode, reading interval, H2S limit, anomaly thresholds, signer.
      { label: 'Well Testing', route: '/settings/well-testing', vertical: 'well_testing' },
      { label: 'Earthworks', route: '/settings/earthworks', vertical: 'earthworks' },
      { label: 'Well Servicing', route: '/settings/well-servicing', vertical: 'well_servicing' },
      // GET/PUT /wireline-slickline/settings + per-customer settings (api#1336): ticket mode, day sheet, depth unit, misfire note, signer.
      { label: 'Wireline & Slickline', route: '/settings/wireline-slickline', vertical: 'wireline_slickline' },
      // GET/PUT /downhole/settings (api#1052): the Directional & MWD switch.
      { label: 'Downhole', route: '/settings/downhole', vertical: 'downhole_tools' },
    ],
  },
  {
    // One workspace, tabbed: Metrics and Forms. Cross-vertical, like Safety
    // itself. Deliberately ungated. Rendered by SafetyWorkspace.tsx, which
    // predates this list and keeps its own title and tabs.
    key: 'safety',
    label: 'Safety',
    icon: Shield,
    tabs: [
      { label: 'Metrics', route: '/settings/safety/metrics' },
      { label: 'Forms', route: '/settings/safety/forms' },
    ],
  },
  {
    key: 'people',
    label: 'People & access',
    icon: Users,
    // Members was dropped after fe#690 and fe#691: a read-only copy of /team, which lists,
    // searches and opens the same profiles and does more besides. Its URL
    // redirects to /team in App.tsx.
    tabs: [
      { label: 'Roles & Permissions', route: '/settings/team/roles' },
      // The tenant's `positions` and their line item types. Cross-vertical and
      // ungated, like Equipment Types: the list is ungated on the API and the
      // page hides its writes itself (`adminOnly` would admit owner, whom the
      // API's requireRole('admin') refuses). NOT trucking_positions, which is
      // on the Trucking tab — and /settings/team/positions still redirects there.
      { label: 'Positions', route: '/settings/positions' },
    ],
  },
  {
    key: 'ai-integrations',
    label: 'AI & integrations',
    icon: Bot,
    tabs: [
      { label: 'AI Agent', route: '/settings/ai-agent' },
      // The billing pre-flight agent (api#908, api#916). Not adminOnly: office
      // staff read the metrics; the mode control inside is strict admin. Casing
      // only, because it checks casing field tickets and nothing else.
      { label: 'Billing agent', route: '/settings/billing-agent', vertical: 'casing_running' },
      // The maintenance agent ("Gus", FE-4; api#944, api#1012), beside Billing
      // agent and gated the same way: office staff read the metrics, the mode
      // and left-on-site days inside are strict admin. Casing only — Gus is
      // casing-first and evaluates no PT, downhole, trucking or rentals asset.
      { label: 'Maintenance agent', route: '/settings/maintenance-agent', vertical: 'casing_running' },
      // Scout, the Sales & CRM agent (api#1079). Gated like Billing agent:
      // admins and office staff read it, the mode inside is strict admin.
      // Cross-vertical — every vertical has customers — so no `vertical`.
      { label: 'Scout', route: '/settings/scout-agent' },
      // Month-to-date AI spend against the tenant's allowance, and the tenant's own
      // Anthropic key (fieldiq-api 0583). Cross-vertical: the cap is per tenant.
      // Not adminOnly: office staff read the meter; the key form inside is admin only.
      { label: 'AI usage', route: '/settings/ai-usage' },
      // QuickBooks mapping is a drill-in from this tab
      // (/settings/integrations/quickbooks-mapping), not a tab of its own —
      // the Integrations tab stays selected while it is open.
      { label: 'Integrations', route: '/settings/integrations' },
      // Motive / Samsara (API 0440), Geotab (0442). Cross-vertical: deliberately no `vertical`.
      // The API is strict admin; adminOnly also admits `owner`, whom the page
      // shows the refusal, as Learned rules does. Its route sits under the
      // Integrations tab's, so activeTab() picks the longer match.
      { label: 'GPS Connectors', route: '/settings/integrations/gps', adminOnly: true },
      { label: 'API Keys', route: '/settings/api-keys' },
      // adminOnly resolves through isAdminRole, which admits `owner`; the API
      // is strict admin and refuses an owner. The page renders that refusal
      // as a refusal, not as "no rules".
      { label: 'Learned rules', route: '/settings/learned-rules', adminOnly: true },
    ],
  },
  {
    key: 'activity',
    label: 'Activity',
    icon: ScrollText,
    tabs: [{ label: 'Audit Log', route: '/settings/audit-log' }],
  },
];

export interface SettingsVisibility {
  hasVertical: (key: string) => boolean;
  isAdmin: boolean;
}

export function visibleTabs(ws: SettingsWorkspace, v: SettingsVisibility): SettingsTab[] {
  return ws.tabs.filter(
    (t) => (!t.vertical || v.hasVertical(t.vertical)) && (!t.adminOnly || v.isAdmin),
  );
}

/** Workspaces with at least one visible tab, each carrying only those tabs. */
export function visibleWorkspaces(v: SettingsVisibility): SettingsWorkspace[] {
  return SETTINGS_WORKSPACES
    .map((ws) => ({ ...ws, tabs: visibleTabs(ws, v) }))
    .filter((ws) => ws.tabs.length > 0);
}

/** A tab owns its own URL and anything drilled into beneath it. */
export function tabMatches(tab: SettingsTab, pathname: string): boolean {
  return pathname === tab.route || pathname.startsWith(`${tab.route}/`);
}

/**
 * The one tab a path selects. GPS Connectors' route nests under Integrations',
 * so both `tabMatches` it; the longest route wins, or a strip would mark two
 * tabs selected.
 */
export function activeTab(tabs: SettingsTab[], pathname: string): SettingsTab | undefined {
  return tabs
    .filter((t) => tabMatches(t, pathname))
    .sort((a, b) => b.route.length - a.route.length)[0];
}

export function workspaceMatches(ws: SettingsWorkspace, pathname: string): boolean {
  return ws.tabs.some((t) => tabMatches(t, pathname));
}

export function getWorkspace(key: string): SettingsWorkspace {
  const ws = SETTINGS_WORKSPACES.find((w) => w.key === key);
  if (!ws) throw new Error(`Unknown settings workspace: ${key}`);
  return ws;
}
